import PropTypes from 'prop-types'
import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'

export const TabContext = createContext({})

export const useTabContext = () => {
  const provided = useContext(TabContext)
  return provided
}

const getAvailableTabId = (tabs, requestedTabId, fallbackTabId = null) => {
  if (tabs?.some((tab) => tab?.key === requestedTabId)) {
    return requestedTabId
  }
  if (fallbackTabId && tabs?.some((tab) => tab?.key === fallbackTabId)) {
    return fallbackTabId
  }
  return tabs?.[0]?.key || null
}

export function TabProvider({ tabs, children, initialTabId = null, activeTabId = undefined, onTabChange = null }) {
  const tabKeys = useMemo(() => (tabs || []).map((tab) => tab?.key).join(','), [tabs])
  const [uncontrolledActiveTab, setUncontrolledActiveTab] = useState(() => initialTabId || tabs?.[0]?.key || null)
  const isControlled = activeTabId !== undefined
  const activeTab = isControlled ? getAvailableTabId(tabs, activeTabId) : uncontrolledActiveTab
  const notifiedFallback = useRef(null)

  useEffect(() => {
    if (isControlled) {
      return
    }
    setUncontrolledActiveTab((currentTab) => {
      if (currentTab && tabs?.some((tab) => tab?.key === currentTab)) {
        return currentTab
      }
      if (initialTabId && tabs?.some((tab) => tab?.key === initialTabId)) {
        return initialTabId
      }
      return tabs?.[0]?.key || null
    })
  }, [tabKeys, initialTabId, tabs, isControlled])

  useEffect(() => {
    if (!isControlled || activeTabId === activeTab) {
      notifiedFallback.current = null
      return
    }
    if (
      !onTabChange ||
      (notifiedFallback.current?.requestedTabId === activeTabId && notifiedFallback.current?.tabId === activeTab)
    ) {
      return
    }

    // Notify once per fallback, even if the parent recreates its callback without correcting the requested tab.
    notifiedFallback.current = { requestedTabId: activeTabId, tabId: activeTab }
    onTabChange(activeTab)
  }, [isControlled, activeTabId, activeTab, onTabChange])

  const handleTabChange = useCallback(
    (tabId) => {
      const requestedTabId = typeof tabId === 'function' ? tabId(activeTab) : tabId
      const availableTabId = getAvailableTabId(tabs, requestedTabId, isControlled ? null : initialTabId)
      if (!isControlled) {
        setUncontrolledActiveTab(availableTabId)
      }
      onTabChange?.(availableTabId)
    },
    [tabs, activeTab, isControlled, initialTabId, onTabChange],
  )
  const setActiveTab = isControlled || onTabChange ? handleTabChange : setUncontrolledActiveTab

  const provided = useMemo(
    () => ({
      tabs,
      activeTab,
      setActiveTab,
    }),
    [tabs, activeTab, setActiveTab],
  )

  return <TabContext.Provider value={provided}>{children}</TabContext.Provider>
}

TabProvider.propTypes = {
  tabs: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string,
      label: PropTypes.node,
    }),
  ),
  children: PropTypes.node,
  /** Initial preference for uncontrolled tabs; does not replace an available current selection. */
  initialTabId: PropTypes.string,
  /** Set to opt into controlled tabs. Unavailable IDs (including null) select the first available tab. */
  activeTabId: PropTypes.string,
  /** Receives selections and controlled fallbacks, or null when no tabs are available. */
  onTabChange: PropTypes.func,
}

export function TabSwitch({ children = null, tab }) {
  const { activeTab } = useTabContext()

  if (activeTab && activeTab !== tab) {
    return null
  }

  return children
}

TabSwitch.propTypes = {
  children: PropTypes.node,
  tab: PropTypes.string.isRequired,
}
